import { Injectable } from "@angular/core";
import { getEmployeeAmountSegment } from "@bca-company-data/company-data.constants";
import { SignatoryStatus } from "@bca-company-data/models/signatory-status-response";
import * as companyDataActions from "@bca-company-data/store/company-data.actions";
import { CompanyDataActionTypes } from "@bca-company-data/store/company-data.actions";
import { ApiService } from "@bca-core/services/api.service";
import { MessageType } from "@bca-shared/models/_enums";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction, createActions } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import { selectUsers } from "@bca-store/store.selectors";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { EMPTY, of, zip } from "rxjs";
import {
  catchError,
  filter,
  mergeMap,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";

@Injectable()
export class CompanyDataEffects {
  fetchCompany = createEffect(() =>
    this._actions.pipe(
      ofType(CompanyDataActionTypes.FetchCompany),
      filter(
        (action: companyDataActions.FetchCompany) => action.payload != null
      ),
      switchMap((action: companyDataActions.FetchCompany) => {
        const { kvkNumber, searchTerm } = action.payload;
        this._store$.dispatch(
          this._createAction(
            new companyDataActions.CompanyFetching({ fetching: true })
          )
        );
        return zip(
          this._apiService.getCompanyCheck(kvkNumber).pipe(
            catchError((error) => {
              const message = {
                body: "NOTIFICATION.ERROR.COMPANY_CHECK",
                type: MessageType.ERROR,
                context: { error },
                originalException: error,
              };
              this._store$.dispatch(
                this._createAction(new appActions.SetMessage(message))
              );
              return EMPTY;
            })
          ),
          of({ kvkNumber, searchTerm })
        );
      }),
      filter(([response]) => response.success),
      switchMap(([, params]) => {
        const { kvkNumber, searchTerm } = params;
        return zip(
          this._apiService.getCompanyProfile(kvkNumber).pipe(
            catchError((error) => {
              const message = {
                body: "NOTIFICATION.ERROR.COMPANY_PROFILE",
                type: MessageType.ERROR,
                context: { error },
                originalException: error,
              };
              this._store$.dispatch(
                this._createAction(new appActions.SetMessage(message))
              );
              return EMPTY;
            })
          ),
          of(searchTerm)
        );
      }),
      filter(([response]) => response.success),
      withLatestFrom(this._store$.pipe(select(selectUsers))),
      mergeMap(([[response, searchTerm], users]) => {
        const { bedrijfsGegevens } = response;
        const { value: employeeAmount } = getEmployeeAmountSegment(
          bedrijfsGegevens.employeeAmount
        );
        const company = { ...bedrijfsGegevens, searchTerm, employeeAmount };
        const representative = users.length === 1 ? [...users].pop() : null;

        return this._createActions([
          new appActions.DeleteMessage(),
          representative
            ? new companyDataActions.SelectCompany({
                ...company,
                representative,
              })
            : new companyDataActions.SelectCompany(company),
          new companyDataActions.CompanyFetching({ fetching: false }),
          new companyDataActions.SelectUserFromHistoryChecked({
            checked: false,
          }),
          new companyDataActions.DifferentContactPersonChecked({
            checked: false,
          }),
        ]);
      })
    )
  );

  validateSignatory = createEffect(
    () =>
      this._actions.pipe(
        ofType(CompanyDataActionTypes.ValidateSignatory),
        filter(
          (action: companyDataActions.ValidateSignatory) =>
            action.payload != null
        ),
        switchMap((action: companyDataActions.ValidateSignatory) => {
          const companySignatory = action.payload;
          return zip(
            this._apiService.validateSignatory(companySignatory).pipe(
              catchError(() => {
                this._store$.dispatch(
                  this._createAction(
                    new companyDataActions.SetSignatoryStatus({
                      status: SignatoryStatus.ONBEKEND,
                    })
                  )
                );
                return EMPTY;
              })
            )
          );
        }),
        switchMap(([params]: any): any => {
          const { success, signatoryStatus } = params;
          this._store$.dispatch(
            this._createAction(
              new companyDataActions.SetSignatoryStatus({
                status: signatoryStatus,
              })
            )
          );

          return EMPTY;
        })
      ),
    { dispatch: false }
  );

  private _createAction(action: IAction): IAction {
    return createAction(action, CompanyDataEffects);
  }

  private _createActions(actions: IAction[]): IAction[] {
    return createActions(actions, CompanyDataEffects);
  }

  constructor(
    private _actions: Actions,
    private _store$: Store<IRootState>,
    private _apiService: ApiService
  ) {}
}
